class LPHeader extends HTMLElement {
  closeTimer = null;

  clickOpenTargetId = null;

  suppressedHoverTarget = null;

  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('focusout', this.onFocusOut);
    this.addEventListener('pointerover', this.onPointerOver);
    this.addEventListener('pointerout', this.onPointerOut);
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('resize', this.updateMegaMenuOffset);
    this.updateMegaMenuOffset();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('focusout', this.onFocusOut);
    this.removeEventListener('pointerover', this.onPointerOver);
    this.removeEventListener('pointerout', this.onPointerOut);
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('resize', this.updateMegaMenuOffset);
    this.cancelPendingClose();
  }

  onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const item = event.target.closest('[data-lp-menu-item]');
    if (!item || !this.contains(item)) return;

    const targetId = item.getAttribute('data-lp-menu-target');
    if (!targetId) return;

    event.preventDefault();

    // TODO: Firefox touch simulation still does not reliably close on a second tap.
    // Outside clicks close the menu, so this is acceptable until touch handling is revisited.
    if (item.hasAttribute('data-open') && (item.dataset.openSource === 'click' || this.clickOpenTargetId === targetId)) {
      this.closeMenu(item);
      this.suppressedHoverTarget = targetId;
      return;
    }

    this.openMenu(item, { immediateClose: true, source: 'click' });
  };

  onFocusIn = (event) => {
    if (!(event.target instanceof Element)) return;

    const item = event.target.closest('[data-lp-menu-item]');
    if (item) {
      const targetId = item.getAttribute('data-lp-menu-target');
      if (targetId && targetId === this.clickOpenTargetId && item.hasAttribute('data-open')) return;

      this.openMenu(item, { immediateClose: true, source: 'focus' });
    }
  };

  onPointerOver = (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.target.closest('[data-lp-mega-menu]')) {
      this.cancelPendingClose();
      return;
    }

    const item = event.target.closest('[data-lp-menu-item]');
    if (item) {
      const targetId = item.getAttribute('data-lp-menu-target');
      if ('pointerType' in event && event.pointerType && event.pointerType !== 'mouse') return;
      if (targetId && targetId === this.suppressedHoverTarget) return;
      if (targetId && targetId === this.clickOpenTargetId && item.hasAttribute('data-open')) return;
      if (targetId && item.hasAttribute('data-open') && item.dataset.openSource === 'click') return;

      this.suppressedHoverTarget = null;
      this.openMenu(item, { immediateClose: false, source: 'hover' });
    }
  };

  onPointerOut = (event) => {
    if (!(event.target instanceof Element)) return;

    const nav = event.target.closest('.lp-header__nav');
    if (!nav) return;

    if (event.relatedTarget instanceof Node && nav.contains(event.relatedTarget)) return;

    this.suppressedHoverTarget = null;
    this.closeMenus();
  };

  onFocusOut = (event) => {
    if (!(event.target instanceof Element)) return;

    window.requestAnimationFrame(() => {
      if (!this.contains(document.activeElement)) {
        this.closeMenus();
      }
    });
  };

  onDocumentClick = (event) => {
    if (!(event.target instanceof Element)) return;

    if (!this.contains(event.target)) {
      this.closeMenus();
    }
  };

  onKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.closeMenus();
    }
  };

  closeMenus(except = null) {
    this.cancelPendingClose();

    this.querySelectorAll('[data-lp-menu-item][data-open]').forEach((item) => {
      if (item !== except) {
        this.closeMenu(item);
      }
    });
  }

  openMenu(item, options = {}) {
    const targetId = item.getAttribute('data-lp-menu-target');
    if (!targetId) {
      this.suppressedHoverTarget = null;

      if (options.immediateClose) {
        this.closeMenus();
      } else {
        this.scheduleCloseMenus();
      }
      return;
    }

    this.cancelPendingClose();
    this.suppressedHoverTarget = null;
    this.updateMegaMenuOffset();
    this.closeMenus(item);
    item.setAttribute('data-open', '');
    item.dataset.openSource = options.source || 'hover';
    this.clickOpenTargetId = options.source === 'click' ? targetId : null;
    item.querySelector('[data-lp-menu-toggle]')?.setAttribute('aria-expanded', 'true');
    this.querySelector(`#${CSS.escape(targetId)}`)?.setAttribute('data-open', '');
  }

  closeMenu(item) {
    const targetId = item.getAttribute('data-lp-menu-target');

    item.removeAttribute('data-open');
    if (this.clickOpenTargetId === targetId) {
      this.clickOpenTargetId = null;
    }
    delete item.dataset.openSource;
    item.querySelector('[data-lp-menu-toggle]')?.setAttribute('aria-expanded', 'false');

    if (targetId) {
      this.querySelector(`#${CSS.escape(targetId)}`)?.removeAttribute('data-open');
    }
  }

  updateMegaMenuOffset = () => {
    const nav = this.querySelector('.lp-header__nav');
    if (!(nav instanceof HTMLElement)) return;

    nav.style.setProperty('--lp-header-nav-left', `${Math.round(nav.getBoundingClientRect().left)}px`);
  };

  scheduleCloseMenus() {
    this.cancelPendingClose();
    this.closeTimer = window.setTimeout(() => {
      this.closeTimer = null;
      this.closeMenus();
    }, 200);
  }

  cancelPendingClose() {
    if (this.closeTimer) {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }
}

if (!customElements.get('lp-header')) {
  customElements.define('lp-header', LPHeader);
}
